import { Editor, useEditorState } from '@tiptap/react'
import { Button, Dropdown, Tooltip } from 'antd'
import { Grid1 } from 'iconsax-react'

import { useTranslation } from 'react-i18next'

import type { MenuProps } from 'antd'

type Props = {
  editor: Editor
}

export const MenuTableButtons = ({ editor }: Props) => {
  const { t } = useTranslation()

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isTable: ctx.editor.isActive('table') ?? false
      }
    }
  })

  const insertTable = (rows: number, cols: number) => {
    editor
      .chain()
      .focus()
      .insertTable({ rows, cols, withHeaderRow: true })
      .run()
  }

  const addColumnBefore = () => {
    editor.chain().focus().addColumnBefore().run()
  }

  const addColumnAfter = () => {
    editor.chain().focus().addColumnAfter().run()
  }

  const deleteColumn = () => {
    editor.chain().focus().deleteColumn().run()
  }

  const addRowBefore = () => {
    editor.chain().focus().addRowBefore().run()
  }

  const addRowAfter = () => {
    editor.chain().focus().addRowAfter().run()
  }

  const deleteRow = () => {
    editor.chain().focus().deleteRow().run()
  }

  const deleteTable = () => {
    editor.chain().focus().deleteTable().run()
  }

  const toggleHeaderRow = () => {
    editor.chain().focus().toggleHeaderRow().run()
  }

  const toggleHeaderColumn = () => {
    editor.chain().focus().toggleHeaderColumn().run()
  }

  const toggleHeaderCell = () => {
    editor.chain().focus().toggleHeaderCell().run()
  }

  const mergeCells = () => {
    editor.chain().focus().mergeCells().run()
  }

  const splitCell = () => {
    editor.chain().focus().splitCell().run()
  }

  const insertTableMenuItems: MenuProps['items'] = [
    {
      key: '2x2',
      label: '2x2',
      onClick: () => insertTable(2, 2)
    },
    {
      key: '3x3',
      label: '3x3',
      onClick: () => insertTable(3, 3)
    },
    {
      key: '4x4',
      label: '4x4',
      onClick: () => insertTable(4, 4)
    },
    {
      key: '5x5',
      label: '5x5',
      onClick: () => insertTable(5, 5)
    }
  ]

  const tableMenuItems: MenuProps['items'] = [
    {
      key: 'add-column-before',
      label: t('editor.add_column_before'),
      onClick: addColumnBefore
    },
    {
      key: 'add-column-after',
      label: t('editor.add_column_after'),
      onClick: addColumnAfter
    },
    {
      key: 'delete-column',
      label: t('editor.delete_column'),
      onClick: deleteColumn
    },
    {
      type: 'divider'
    },
    {
      key: 'add-row-before',
      label: t('editor.add_row_before'),
      onClick: addRowBefore
    },
    {
      key: 'add-row-after',
      label: t('editor.add_row_after'),
      onClick: addRowAfter
    },
    {
      key: 'delete-row',
      label: t('editor.delete_row'),
      onClick: deleteRow
    },
    {
      type: 'divider'
    },
    {
      key: 'toggle-header-row',
      label: t('editor.toggle_header_row'),
      onClick: toggleHeaderRow
    },
    {
      key: 'toggle-header-column',
      label: t('editor.toggle_header_column'),
      onClick: toggleHeaderColumn
    },
    {
      key: 'toggle-header-cell',
      label: t('editor.toggle_header_cell'),
      onClick: toggleHeaderCell
    },
    {
      type: 'divider'
    },
    {
      key: 'merge-cells',
      label: t('editor.merge_cells'),
      onClick: mergeCells
    },
    {
      key: 'split-cell',
      label: t('editor.split_cell'),
      onClick: splitCell
    },
    {
      type: 'divider'
    },
    {
      key: 'delete-table',
      label: t('editor.delete_table'),
      onClick: deleteTable,
      danger: true
    }
  ]

  return (
    <>
      <Dropdown menu={{ items: insertTableMenuItems }} trigger={['click']}>
        <Tooltip title={t('editor.insert_table')}>
          <Button icon={<Grid1 size={14} />} />
        </Tooltip>
      </Dropdown>

      {editorState.isTable && (
        <Dropdown menu={{ items: tableMenuItems }} trigger={['click']}>
          <Tooltip title={t('editor.table_options')}>
            <Button icon={<Grid1 size={14} />} type="primary" />
          </Tooltip>
        </Dropdown>
      )}
    </>
  )
}
